import type { BenefitDetailDto, IncomeDto, PersonDto } from "./benefit-types"
import { hasMixedIncomeAdjustments, savedMonthlyAdjustedIncome, savedMonthlyIncome } from "./income-summary"
import { incomeHistoryLabels } from "./income"
import { fmtDate } from "./benefit-format"

export type IncomeAssessment = {
  status: string
  targetProgramYear?: number | null
  effectiveProgramYear?: number | null
  message: string | null
  evidenceStatus: string | null
  monthlyIncome: number | null
  householdSize: number | null
  noIncomeConfirmed: boolean
  membersRequiringIncomeEvidence: string[]
  concurrencyToken: string | null
  programs: {
    programId: number; name: string; programYear: number; revision: number | null
    benefitProgramInstance: { program: number; year: number; revision: number } | null
    status: string; monthlyIncome: number | null; householdSize: number | null
    incomeLimit: number | null; amountOver: number | null; reason: string | null
  }[]
}
export type IncomeAssessmentState = { assessment: IncomeAssessment | null; loading: boolean; error: string | null }

export function incomeEvidenceLabel(assessment: IncomeAssessment | null): string {
  switch (assessment?.evidenceStatus) {
    case "reportedAmounts": return "Reported amounts"
    case "explicitNoIncome": return "Explicit no-income confirmation"
    case "reportedAmountsAndExplicitNoIncome": return "Reported amounts + no-income answers"
    case "unanswered": return "Needs income answers"
    case "inconsistent": return "Inconsistent saved evidence"
    default: return "Unavailable"
  }
}

/** No demographic source byte or empty collection constitutes a no-income confirmation. */
export function memberIncomeEvidence(person: PersonDto, assessment: IncomeAssessment | null): string {
  if (person.incomeCollection.length > 0) return "Saved income entries"
  if (assessment?.membersRequiringIncomeEvidence?.includes(person.id)) return "Needs an income answer"
  if (assessment?.noIncomeConfirmed === true && assessment.membersRequiringIncomeEvidence?.length === 0 &&
    ["explicitNoIncome", "reportedAmountsAndExplicitNoIncome"].includes(assessment.evidenceStatus ?? "")) return "No income confirmed"
  return "No income reported; confirmation unavailable"
}

/** Preserve the whole server-specified mutation scope, or offer no confirmation at all. */
export function noIncomeConfirmationMembers(benefit: BenefitDetailDto, assessment: IncomeAssessment | null): PersonDto[] {
  if (!benefit.concurrencyToken || assessment?.concurrencyToken !== benefit.concurrencyToken || assessment.evidenceStatus !== "unanswered") return []
  const ids = assessment.membersRequiringIncomeEvidence
  if (!Array.isArray(ids) || ids.length === 0 || new Set(ids).size !== ids.length) return []
  const people = benefit.personCollection.filter(person => ids.includes(person.id))
  return people.length === ids.length && new Set(people.map(person => person.id)).size === ids.length && people.every(person => person.incomeCollection.length === 0) ? people : []
}

export function incomeRecordedDate(value: string): string {
  const day = value?.slice(0, 10)
  const parsed = new Date(`${day}T00:00:00Z`)
  return /^\d{4}-\d{2}-\d{2}$/.test(day ?? "") && Number.isFinite(Date.parse(value)) && parsed.getUTCFullYear() > 0 && parsed.toISOString().slice(0, 10) === day
    ? fmtDate(day) : `Recorded: ${value || "Unavailable"}`
}

export function incomePeriodLabel(amounts: readonly { date: string }[]): string {
  if (amounts.length === 0) return "Not recorded"
  const months = [...new Set(amounts.map(amount => amount.date?.slice(0, 7)))].sort()
  if (months.some(month => !/^\d{4}-(0[1-9]|1[0-2])$/.test(month ?? ""))) return "Dates unavailable"
  const labels = months.map(month => new Date(`${month}-01T00:00:00Z`).toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" }))
  const serials = months.map(month => Number(month!.slice(0, 4)) * 12 + Number(month!.slice(5, 7)))
  // A range is truthful only when every intervening month was actually returned.
  return months.length > 1 && serials.every((value, index) => index === 0 || value === serials[index - 1]! + 1)
    ? `${labels[0]} – ${labels.at(-1)}` : labels.join(", ")
}

export function householdIncomePeriods(people: readonly PersonDto[]): string {
  const periods = [...new Set(people.flatMap(person => person.incomeCollection.map(income => incomePeriodLabel(income.incomeAmountCollection))))]
  return periods.length === 0 ? "Not recorded" : periods.length === 1 ? periods[0]! : "Mixed — see each source"
}

/** Display all stored adjustment flags, including combinations a single-choice editor cannot represent. */
export function savedAdjustmentLabel(income: IncomeDto): string {
  if (![income.a1, income.a2, income.a3, income.aNone].every(flag => typeof flag === "boolean")) return "Unavailable"
  const flags = [income.a1 && "20%", income.a2 && "15% (legacy; no effect)", income.a3 && "10%", income.aNone && "None"].filter(Boolean)
  return flags.length ? flags.join(" + ") : "Not answered"
}

export function savedIncomeFigures(income: IncomeDto): { gross: number | null; monthly: number | null; monthlyAdjusted: number | null } {
  const amounts = income.incomeAmountCollection
  const gross = amounts.length > 0 && amounts.every(amount => typeof amount.amount === "number" && Number.isFinite(amount.amount))
    ? amounts.reduce((total, item) => total + item.amount, 0) : null
  const knownHistory = Object.hasOwn(incomeHistoryLabels, income.incomeType)
  const knownFlags = [income.a1, income.a2, income.a3, income.aNone].every(flag => typeof flag === "boolean")
  const finite = (value: number) => Number.isFinite(value) ? value : null
  return {
    gross: gross === null ? null : finite(gross),
    monthly: gross === null || !knownHistory ? null : finite(savedMonthlyIncome(income)),
    monthlyAdjusted: gross === null || !knownHistory || !knownFlags ? null : finite(savedMonthlyAdjustedIncome(income)),
  }
}

/** The current whole-member write takes one adjustment enum, not the original bit set. */
export function incomeEditBlocker(person: PersonDto): string | null {
  if (person.incomeCollection.some(income => ![income.a1, income.a2, income.a3, income.aNone].every(flag => typeof flag === "boolean"))) return "Adjustment flags are unavailable. Reload before editing these saved sources."
  if (person.incomeCollection.some(hasMixedIncomeAdjustments)) return "Legacy mixed adjustment flags cannot be preserved by this editor. These saved sources remain read-only."
  const ids = person.incomeCollection.map(income => income.id)
  if (new Set(ids).size !== ids.length) return "Duplicate saved source identifiers cannot be edited safely. All returned sources are shown."
  return null
}
